import * as React from 'react';
import {Navigate, Route, Routes as ReactRouterRoutes} from 'react-router-dom';

import BannerLayout from 'common/report/layout/banner-layout/outlet';
import {EmptySidenavLayoutOutlet, GlobalSidenavLayoutOutlet} from 'iron/routes/global-sidenav/layout/outlet';
import {isPublicMode} from 'common/report/util';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {ProjectFeatureFlag} from 'common/report/constants';
import {RoutesLayout} from 'iron/routes/routes-layout';

import {lazyWithErrorBoundary} from './components/lazy-loader-error-boundary';

export function Routes() {
  const currentProject = isPublicMode() ? undefined : useCurrentProject().get().value;

  const isHomeDisabled = currentProject?.hasFeatureFlag(ProjectFeatureFlag.DisableHome);

  const ReportingRoutes = (
    <>
      <Route
        path='home/*'
        element={
          isHomeDisabled ? (
            <Navigate to={`../boards#discover`} />
          ) : (
            lazyWithErrorBoundary(() => import(/* webpackChunkName: "home" */ `apps/home/src`))
          )
        }
      />
      {/* Core apps and board goes on the top */}
      <Route
        path='boards/embed?/*'
        element={lazyWithErrorBoundary(
          () =>
            import(
              /* webpackChunkName: "boards" */
              /* webpackPreload: true */
              `apps/boards/src`
            ),
        )}
      />
      <Route
        path='insights/embed?/*'
        element={lazyWithErrorBoundary(
          () =>
            import(
              /* webpackChunkName: "core-report" */
              /* webpackPreload: true */
              `apps/insights/src`
            ),
          `insights`,
        )}
      />
      <Route
        path='funnels/embed?/*'
        element={lazyWithErrorBoundary(
          () =>
            import(
              /* webpackChunkName: "core-report" */
              /* webpackPreload: true */
              `apps/insights/src`
            ),
          `funnels`,
        )}
      />
      <Route
        path='flows/embed?/*'
        element={lazyWithErrorBoundary(
          () =>
            import(
              /* webpackChunkName: "core-report" */
              /* webpackPreload: true */
              `apps/flows/src`
            ),
        )}
      />
      <Route
        path='retention/embed?/*'
        element={lazyWithErrorBoundary(
          () =>
            import(
              /* webpackChunkName: "core-report" */
              /* webpackPreload: true */
              `apps/insights/src`
            ),
          `retention`,
        )}
      />
      <Route
        path='metric-tree/embed?/*'
        element={lazyWithErrorBoundary(
          () =>
            import(
              /* webpackChunkName: "metric-tree" */
              `apps/metric-tree/src`
            ),
        )}
      />
      <Route
        path='experiments/embed?/*'
        element={lazyWithErrorBoundary(
          () =>
            import(
              /* webpackChunkName: "core-report" */
              /* webpackPreload: true */
              `apps/experiments/src`
            ),
        )}
      />
      <Route
        path='feature-flags/embed?/*'
        element={lazyWithErrorBoundary(
          () =>
            import(
              /* webpackChunkName: "core-report" */
              /* webpackPreload: true */
              `apps/feature-flags/src`
            ),
        )}
      />
      <Route
        path='heatmaps/embed?/*'
        element={lazyWithErrorBoundary(
          () =>
            import(
              /* webpackChunkName: "core-report" */
              /* webpackPreload: true */
              `apps/heat-maps/src`
            ),
        )}
      />
      <Route
        path='session-replay/embed?/*'
        element={lazyWithErrorBoundary(
          () =>
            import(
              /* webpackChunkName: "core-report" */
              /* webpackPreload: true */
              `apps/session-replay/src`
            ),
        )}
      />
    </>
  );

  const OtherRoutes = (
    <Route element={<BannerLayout />}>
      {/* User & Event */}
      <Route
        path='events/embed?/*'
        element={lazyWithErrorBoundary(() => import(/* webpackChunkName: "events" */ `apps/events/src`))}
      />
      <Route
        path='users/embed?/*'
        element={lazyWithErrorBoundary(() => import(/* webpackChunkName: "users" */ `apps/users/src`))}
      />
      <Route
        path='profile/embed?/*'
        element={lazyWithErrorBoundary(() => import(/* webpackChunkName: "profile" */ `apps/profile/src`))}
      />

      {/* data management apps */}
      {/* currently used for cohorts */}
      <Route
        path='entity-management/*'
        element={lazyWithErrorBoundary(
          () => import(/* webpackChunkName: "entity-management" */ `apps/entity-management/src`),
        )}
      />
      <Route
        path='integrations/*'
        element={lazyWithErrorBoundary(() => import(/* webpackChunkName: "integrations" */ `apps/integrations/src`))}
      />

      {/* apps in the app dropdown menu */}
      <Route
        path='signal/embed?/*'
        element={lazyWithErrorBoundary(() => import(/* webpackChunkName: "signal" */ `apps/signal/src`))}
      />
      <Route
        path='impact/embed?/*'
        element={lazyWithErrorBoundary(() => import(/* webpackChunkName: "impact" */ `apps/impact/src`))}
      />
      <Route
        path='experiment-reporting/embed?/*'
        element={lazyWithErrorBoundary(
          () => import(/* webpackChunkName: "experiment-reporting" */ `apps/experiment-reporting/src`),
        )}
      />

      <Route
        path='jql-console/embed?/*'
        element={lazyWithErrorBoundary(() => import(/* webpackChunkName: "jql-console" */ `apps/jql-console/src`))}
      />

      {/* settings related apps */}
      <Route
        path='views/*'
        element={lazyWithErrorBoundary(() => import(/* webpackChunkName: "views" */ `apps/views/src`))}
      />
    </Route>
  );

  const StandaloneRoutes = (
    <>
      {/* these apps have sidenavs that they render by themselves */}
      <Route
        path='lexicon/*'
        element={lazyWithErrorBoundary(() => import(/* webpackChunkName: "lexicon" */ `apps/lexicon/src`))}
      />
      <Route
        path='settings/*'
        element={lazyWithErrorBoundary(() => import(/* webpackChunkName: "settings" */ `apps/settings/src`))}
      />
    </>
  );

  const AllRoutes = (
    <>
      <Route
        path='/public/:uuid'
        element={lazyWithErrorBoundary(
          () => import(/* webpackChunkName: "public-boards" */ `apps/boards/src/dashboards-app/public-mode/app`),
        )}
      />
      <Route
        path='/dashboard-digest'
        element={lazyWithErrorBoundary(
          () => import(/* webpackChunkName: "digest-boards" */ `apps/boards/src/dashboards-app/digest-mode/app`),
        )}
      />
      <Route path='/project/:projectId/view?/:workspaceId?/app' element={<GlobalSidenavLayoutOutlet />}>
        {ReportingRoutes}
        {OtherRoutes}
        {StandaloneRoutes}
      </Route>
      <Route path='/organization' element={<EmptySidenavLayoutOutlet />}>
        <Route
          path='*'
          element={lazyWithErrorBoundary(
            () => import(/* webpackChunkName: "admin-org-settings" */ `apps/admin-org-settings/src/index`),
          )}
        />
      </Route>
    </>
  );

  return (
    <ReactRouterRoutes>
      <Route element={<RoutesLayout />}>{AllRoutes}</Route>
    </ReactRouterRoutes>
  );
}
